export interface ApplicationApi {
  get<T>(path: string): Promise<T>;
  post<TResponse, TRequest = unknown>(path: string, body: TRequest): Promise<TResponse>;
  put<TResponse, TRequest = unknown>(path: string, body: TRequest): Promise<TResponse>;
}

export class ApplicationApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
  }
}

interface ApplicationApiOptions {
  accessToken: string;
  baseUrl: string;
  onSessionExpired(correlationId: string): void;
}

export function createApplicationApi({
  accessToken,
  baseUrl,
  onSessionExpired,
}: ApplicationApiOptions): ApplicationApi {
  async function request<TResponse>(path: string, method: "GET" | "POST" | "PUT", body?: unknown) {
    const correlationId = crypto.randomUUID();
    const response = await fetch(`${baseUrl}/${path.replace(/^\//u, "")}`, {
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "X-Loan-Management-Correlation-Id": correlationId,
      },
      method,
      mode: "cors",
      referrerPolicy: "strict-origin",
    });

    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      const error = new ApplicationApiError(
        response.status,
        "RESPONSE_INVALID",
        "Loan Management received an invalid response",
        correlationId,
      );
      if (response.status === 401) onSessionExpired(correlationId);
      throw error;
    }
    if (!response.ok) {
      const error = parseApplicationError(responseBody, response.status, correlationId);
      if (response.status === 401) onSessionExpired(error.correlationId);
      throw error;
    }

    return responseBody as TResponse;
  }

  return {
    get: <T>(path: string) => request<T>(path, "GET"),
    post: <TResponse, TRequest>(path: string, body: TRequest) =>
      request<TResponse>(path, "POST", body),
    put: <TResponse, TRequest>(path: string, body: TRequest) =>
      request<TResponse>(path, "PUT", body),
  };
}

function parseApplicationError(
  value: unknown,
  status: number,
  fallbackCorrelationId: string,
): ApplicationApiError {
  const error = typeof value === "object" && value !== null
    ? value as Record<string, unknown>
    : {};

  return new ApplicationApiError(
    status,
    typeof error.code === "string" ? error.code : "REQUEST_FAILED",
    typeof error.message === "string" ? error.message : "Loan Management could not complete the request",
    typeof error.correlationId === "string" ? error.correlationId : fallbackCorrelationId,
    readFieldErrors(error.fieldErrors),
  );
}

function readFieldErrors(value: unknown): Record<string, string> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
}